// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Grid, Typography } from '@mui/material'

// ** Child Imports
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import ChartComponent from '../visualizations/ChartComponent'

//** Helpers */
import { 
    extractDateFromFilters, 
    fillMissingPoints, 
    getAnalyticsWidgetPayload, 
    getAvailableTimeScales, 
    getGradient, 
    getLabels, 
    getTimeUnit,
    isLessThan30DaysSelected,
    modifyDates} from 'src/lib/utils/analyticsHelpers'
import { 
    conversionMetricOptions, 
    conversionScaleOptions, 
    conversionUnitOptions } from 'src/assets/AnalyticsConfig'
import { appliedDateFilter, arrayToCsv, downloadCsvHelper } from 'src/lib/utils/helpers'

// ** Context
import { useFilter } from 'src/lib/contexts/FilterContext'

// ** Types
import {
    OverviewWidgetFilterOptionsType, 
    ConversionMerticType, 
    ConversionScaleUnitType, 
    ConversionUnitType, 
    WidgetType } from 'src/lib/types/dashboardAnalytics'
import { analyticsConstants } from 'src/lib/utils/constants'
import { Download } from 'react-feather'
import { addDays } from 'date-fns'


const ConversionChart: React.FC<WidgetType> = ({ 
    value,
    isLoading,
    triggerRefresh,
    hasComparison,
    maxTicksLimit=12,
    hideTimeScaleDropdown,
    hideConversionUnitDropdown,
    imposedTimeScale="daily"
}) => {

    const { analyticsFilters } = useFilter();

    const [ selectedConversionMetric, setSelectedConversionMetric ] = 
        useState<ConversionMerticType>("payment_completed");
    const [ selectedConversionUnit, setSelectedConversionUnit ] = useState<ConversionUnitType>("percentage");
    const [ selectedScaleUnit, setSelectedScaleUnit ] = useState<ConversionScaleUnitType>("daily");
    const [ availableConversionScaleOptions, setAvailableConversionScaleOptions ] = 
        useState<OverviewWidgetFilterOptionsType[]>(conversionScaleOptions);

    const plotData = value?.filter((plotArray: any) => !plotArray?.dimension?.includes("compare_to"))?.find((plotArray: any) => plotArray?.dimension?.includes(selectedConversionUnit))?.data;
    const compareToPlotData = value?.filter((plotArray: any) => plotArray?.dimension?.includes("compare_to"))?.find((compareToPlotArray: any) => compareToPlotArray?.dimension?.includes(selectedConversionUnit))?.data;

    const filledPlotData = fillMissingPoints(plotData, selectedScaleUnit, extractDateFromFilters(analyticsFilters, "startDate"), extractDateFromFilters(analyticsFilters, "endDate"));
    const filledCompareToPlotData = fillMissingPoints(compareToPlotData, selectedScaleUnit, extractDateFromFilters(analyticsFilters, "compareToStartDate"), extractDateFromFilters(analyticsFilters, "compareToEndDate"))

    const getDateRangeForTooltip = (isComparison=false) => {
        if (isComparison) {
            return `${addDays(new Date(extractDateFromFilters(analyticsFilters, "compareToStartDate")), 1).toDateString().substring(4, 10)} - ${addDays(new Date(extractDateFromFilters(analyticsFilters, "compareToEndDate")), 0).toDateString().substring(4, 10)}`
        } else {
            return `${addDays(new Date(extractDateFromFilters(analyticsFilters, "startDate")), 1).toDateString().substring(4, 10)} - ${addDays(new Date(extractDateFromFilters(analyticsFilters, "endDate")), 0).toDateString().substring(4, 10)}`
        }
    }
    const converionStepMapper = (conversionMetric: ConversionMerticType, isComparison=false) => {
        switch (conversionMetric) {
            case "payment_completed": return `Sessions converted#${getDateRangeForTooltip(isComparison)}`;
            case "login_completed": return `Login completed#${getDateRangeForTooltip(isComparison)}`;
            case "payment_page_loaded": return `Payment page loaded#${getDateRangeForTooltip(isComparison)}`;
        }
    }

    const dataSets = {
        labels: getLabels(filledPlotData, filledCompareToPlotData),
        datasets: [
            {
                label: converionStepMapper(selectedConversionMetric),
                pointHitRadius: 20,
                pointRadius: 3,
                data: modifyDates(filledPlotData, filledCompareToPlotData),
                cubicInterpolationMode: 'monotone',
                borderColor: function(context: any) {
                    const chart = context.chart;
                    const {ctx, chartArea} = chart;
            
                    if (!chartArea) {
                        return;
                    }
                    return getGradient(ctx, chartArea, "#01CCA7");
                },
                backgroundColor: 'rgb(255, 255, 255)',
                borderJoinStyle: 'round',
                xAxisID: "xAxis1",
            },
            {
                label: converionStepMapper(selectedConversionMetric, true),
                pointHitRadius: 20,
                pointRadius: 3,
                borderDash: [6,3],
                data: modifyDates(filledPlotData, filledCompareToPlotData, true),
                cubicInterpolationMode: 'monotone',
                borderColor: function(context: any) {
                    const chart = context.chart;
                    const {ctx, chartArea} = chart;
            
                    if (!chartArea) {
                        return;
                    }
                    return "#E0E0E0";
                },
                backgroundColor: 'rgb(255, 255, 255)',
                borderJoinStyle: 'round',
                xAxisID: "xAxis2",
            }
        ]
    }

    useEffect(() => {
        setSelectedScaleUnit(imposedTimeScale);
    }, [imposedTimeScale])

    useEffect(() => {
        if (typeof selectedConversionMetric !== "undefined" && typeof selectedScaleUnit !== "undefined") {
            if (!hasComparison) {
                triggerRefresh(getAnalyticsWidgetPayload( 
                    analyticsConstants.FLO_CONVERSION_CHART,
                    extractDateFromFilters(analyticsFilters, "startDate"),
                    extractDateFromFilters(analyticsFilters, "endDate"),
                    selectedScaleUnit,
                    selectedConversionMetric
                ));
            } else {
                triggerRefresh(
                    getAnalyticsWidgetPayload( 
                        analyticsConstants.FLO_CONVERSION_CHART,
                        extractDateFromFilters(analyticsFilters, "startDate"),
                        extractDateFromFilters(analyticsFilters, "endDate"),
                        selectedScaleUnit,
                        selectedConversionMetric
                    ),
                    getAnalyticsWidgetPayload( 
                        analyticsConstants.FLO_CONVERSION_CHART,
                        extractDateFromFilters(analyticsFilters, "compareToStartDate"),
                        extractDateFromFilters(analyticsFilters, "compareToEndDate"),
                        selectedScaleUnit,
                        selectedConversionMetric
                    ),
                );
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConversionMetric, selectedScaleUnit])


    useEffect(() => {
        setSelectedConversionMetric("payment_completed");
        isLessThan30DaysSelected(analyticsFilters) && setSelectedScaleUnit("daily");
        setAvailableConversionScaleOptions(getAvailableTimeScales(analyticsFilters));
    }, [analyticsFilters])

    const handleMetricDropdownClick = (index: number) => {
        setSelectedConversionMetric(
            conversionMetricOptions[index]?.value as
            ConversionMerticType
        );
    }

    const handleUnitDropdownClick = (index: number) => {
        setSelectedConversionUnit(conversionUnitOptions[index].value as
            ConversionUnitType
        );
    }

    const handleScaleDropdownClick = (index: number) => {
        setSelectedScaleUnit(availableConversionScaleOptions[index].value as 
            ConversionScaleUnitType
        );  
    }

    return (
        <>
            <Grid 
                container
                gap={4}
                width={"100%"}
                position={'relative'}
            >
                <div 
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        flexWrap: "wrap"
                    }}
                >
                    <DropdownButton
                        options={conversionMetricOptions} 
                        handleClick={handleMetricDropdownClick}
                        variant="outlined"
                        selectedOption={conversionMetricOptions?.findIndex((option: OverviewWidgetFilterOptionsType, index: number) => option?.value === selectedConversionMetric)}
                    />
                   { !Boolean(hideTimeScaleDropdown) ? <DropdownButton
                        options={availableConversionScaleOptions}
                        handleClick={handleScaleDropdownClick}
                        variant="outlined"
                        selectedOption={availableConversionScaleOptions?.findIndex((option: OverviewWidgetFilterOptionsType, index: number) => option?.value === selectedScaleUnit)}
                    /> : <></> }
                    { !Boolean(hideConversionUnitDropdown) ? <DropdownButton
                        options={conversionUnitOptions}
                        handleClick={handleUnitDropdownClick}
                        variant="outlined"
                    /> : <></> }
                </div>
                <div
                    style={{
                        padding: "0rem",
                        width: "100%",
                        position: "relative",
                        minHeight: "180px",
                        display: 'flex',
                        gap: 8,
                        flexDirection: 'column',
                        justifyContent: "center",
                        alignItems: "center",
                        border: Boolean(value?.length) ? "" : "1px solid #f0f0f0",
                        borderRadius: 20
                    }}
                >
                        <div style={{ minHeight: "340px", height: 'fit-content', width: "100%" }}> 
                            <ChartComponent
                                chartType={"trend_chart"}
                                data={dataSets}
                                isComparison={analyticsFilters?.comparison === "previous"}
                                selectedConversionUnit={selectedConversionUnit}
                                isLoading={isLoading}
                                scaleUnit={getTimeUnit(selectedScaleUnit)}
                                maxTicksLimit={maxTicksLimit}
                            /> 
                        </div>
                        <div className='w-full flex flex-wrap gap-2 justify-end'>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontWeight: 600, 
                                    padding: "0.2rem .5rem", 
                                    borderRadius: '8px',
                                    border: '1px solid #F5F5F5',
                                    backgroundColor: "#fAfAfA",
                                    textAlign: "end",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3
                                }}
                            >
                                <div className={`h-1 w-4 bg-primary-dark rounded-sm`}>
                                </div>
                                {appliedDateFilter(analyticsFilters)} 
                            </Typography>
                            {hasComparison ? <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontWeight: 600, 
                                    padding: "0.2rem .5rem", 
                                    borderRadius: '8px',
                                    border: '1px solid #F5F5F5',
                                    backgroundColor: "#fAfAfA",
                                    textAlign: "end",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3
                                }}
                            >
                                <div className={`h-1 w-4 bg-[#E0E0E0] rounded-sm`}>
                                </div>
                                {appliedDateFilter(analyticsFilters, true)} 
                            </Typography> : <></>}
                        </div>
                    </div> 
            </Grid>
        </>
    )
}

export default ConversionChart;